/*
 * Client Dashboard (read-only, purely additive).
 * Mounted inside the existing portal shell via DashboardPage.jsx.
 * Owns nothing outside itself — deleting this file + its GET endpoint
 * must leave the rest of the platform byte-for-byte unchanged.
 *
 * Layout (per approved mock):
 *   1. Your actions (N)  — 3-column color-coded grid
 *   2. Last 5 orders     — full-width cards with 4-step pipeline
 *   3. Recent messages | DocuVault recent documents  (two columns)
 *
 * No "My Companies" block here — Companies lives in the sidebar.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import {
  CreditCard, FileText, PenTool, MessageSquare, Download, CheckCircle2,
  ArrowRight, Loader2, ChevronRight,
} from 'lucide-react';

const STAGE_LABELS = ['Payment', 'Documents', 'Review', 'Done'];

function fmt(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      hour12: true, timeZone: 'Asia/Kolkata',
    });
  } catch { return ''; }
}

// Colour theme + copy per action kind — matches the mock exactly.
const ACTION_THEMES = {
  payment: {
    icon: CreditCard,
    cardCls: 'bg-red-500/10 border-red-500/40',
    iconWrapCls: 'bg-red-500/20 text-red-300',
    btnCls: 'bg-red-500 hover:bg-red-400 text-white',
    btnLabel: 'Pay now',
  },
  documents: {
    icon: FileText,
    cardCls: 'bg-amber-500/10 border-amber-500/40',
    iconWrapCls: 'bg-amber-500/20 text-amber-300',
    btnCls: 'bg-amber-500 hover:bg-amber-400 text-white',
    btnLabel: 'Upload',
  },
  document_rejected: {
    icon: FileText,
    cardCls: 'bg-amber-500/10 border-amber-500/40',
    iconWrapCls: 'bg-amber-500/20 text-amber-300',
    btnCls: 'bg-amber-500 hover:bg-amber-400 text-white',
    btnLabel: 'Re-upload',
  },
  signature: {
    icon: PenTool,
    cardCls: 'bg-indigo-500/10 border-indigo-500/40',
    iconWrapCls: 'bg-indigo-500/20 text-indigo-300',
    btnCls: 'bg-indigo-500 hover:bg-indigo-400 text-white',
    btnLabel: 'Sign',
  },
  unread_reply: {
    icon: MessageSquare,
    cardCls: 'bg-emerald-500/10 border-emerald-500/40',
    iconWrapCls: 'bg-emerald-500/20 text-emerald-300',
    btnCls: 'bg-emerald-500 hover:bg-emerald-400 text-white',
    btnLabel: 'View',
  },
};

function ActionCard({ a }) {
  const theme = ACTION_THEMES[a.kind] || ACTION_THEMES.unread_reply;
  const Icon = theme.icon;
  const title = a.title || a.label;
  return (
    <Link
      to={a.link}
      className={`flex items-center gap-3 p-4 rounded-lg border transition-colors hover:brightness-125 ${theme.cardCls}`}
      data-testid={`dash-action-${a.kind}`}
    >
      <div className={`flex-none rounded-md p-2 ${theme.iconWrapCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-white leading-snug">{title}</div>
        {(a.subtitle || a.detail) && (
          <div className="text-[11.5px] text-slate-400 leading-snug mt-0.5">
            {a.subtitle}
            {a.subtitle && a.detail && ' · '}
            {a.detail && <span className="italic">&quot;{a.detail}&quot;</span>}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}  /* Link handles nav */
        className={`px-3 py-1.5 rounded text-[12.5px] font-semibold flex-none ${theme.btnCls}`}
      >
        {a.button_label || theme.btnLabel}
      </button>
    </Link>
  );
}

function PipelineStepper({ stageIndex }) {
  return (
    <div className="flex items-center gap-2 mt-3" data-testid="dash-order-pipeline">
      {STAGE_LABELS.map((lbl, i) => {
        const done = i < stageIndex;
        const current = i === stageIndex;
        return (
          <React.Fragment key={lbl}>
            <div className="flex flex-col items-center gap-1 flex-none">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${done || current ? 'bg-emerald-500 text-white' : 'bg-slate-800 border border-slate-700 text-slate-500'}`}>
                {done ? '✓' : current ? '•' : ''}
              </div>
              <span className={`text-[10.5px] ${current ? 'text-emerald-300 font-semibold' : done ? 'text-emerald-500/70' : 'text-slate-500'}`}>{lbl}</span>
            </div>
            {i < STAGE_LABELS.length - 1 && (
              <div className={`flex-1 h-[2px] ${done ? 'bg-emerald-500' : 'bg-slate-800'} mb-4`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OrderCard({ o }) {
  const complete = o.stage_index === 3;
  const waitingOnClient = (o.waiting_on || '').toLowerCase().startsWith('waiting on you');
  const waitingLabel = complete
    ? 'Complete ✓'
    : (waitingOnClient ? o.waiting_on.replace(/^Waiting on you:\s*/, 'Waiting on you — ') : o.waiting_on);
  const waitingCls = complete
    ? 'text-emerald-300'
    : waitingOnClient
      ? 'text-amber-300 font-semibold'
      : 'text-slate-400';

  return (
    <Link
      to={`/orders/${o.id}`}
      className="block p-4 border border-emerald-900/30 rounded-lg bg-[#0d1210] hover:bg-[#12181a] transition-colors"
      data-testid={`dash-order-row-${o.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <div className="text-[14px] font-semibold text-white truncate">{o.product_name}</div>
            <div className="text-[11px] text-slate-500 font-mono">{o.order_ref}</div>
          </div>
        </div>
        <div className={`text-[12px] flex-none ${waitingCls}`}>{waitingLabel}</div>
      </div>
      <PipelineStepper stageIndex={o.stage_index} />
    </Link>
  );
}

export default function ClientDashboardOverviewPage({ onNavigateSection }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback: if the parent didn't wire the section-switcher, fall back to
  // a URL navigation so the buttons still route somewhere sensible.
  const goToSection = (id) => {
    if (typeof onNavigateSection === 'function') onNavigateSection(id);
    else navigate(`/dashboard?section=${id}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/client/dashboard');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load your dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }
  if (error) {
    return <div className="py-16 text-center text-slate-300 text-sm px-4">{error}</div>;
  }

  const actions = data?.actions || [];
  const orders = data?.orders || [];
  const messages = data?.communication || [];
  const documents = data?.documents || [];

  return (
    <div className="space-y-6" data-testid="client-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
        <p className="text-slate-500 text-[13px] mt-0.5">A quick view of your compliance activity</p>
      </div>

      {/* Section 1 — Your actions */}
      <section data-testid="dash-actions">
        <h2 className="text-slate-300 font-semibold text-[13px] mb-2">
          Your actions {actions.length > 0 && <span className="text-slate-500 font-normal">({actions.length})</span>}
        </h2>
        {actions.length === 0 ? (
          <div
            className="flex items-center gap-2 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-[13px]"
            data-testid="dash-actions-empty"
          >
            <CheckCircle2 className="w-4 h-4" /> You&apos;re all caught up ✓
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {actions.map((a, i) => (
              <ActionCard key={`${a.kind}-${a.ticket_id || a.order_id || ''}-${i}`} a={a} />
            ))}
          </div>
        )}
      </section>

      {/* Section 2 — Last 5 orders */}
      <section data-testid="dash-orders">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-slate-300 font-semibold text-[13px]">Last {Math.min(5, orders.length)} orders</h2>
          <button
            onClick={() => goToSection('orders')}
            className="text-emerald-400 hover:text-emerald-300 text-[12px] flex items-center gap-1"
            data-testid="dash-orders-check-all"
          >
            Check all orders <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {orders.length === 0 ? (
          <div className="text-slate-500 text-[13px] p-4 border border-emerald-900/30 rounded-lg bg-[#0d1210]">
            No orders yet.
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map(o => <OrderCard key={o.id} o={o} />)}
          </div>
        )}
      </section>

      {/* Sections 3 & 4 — Two-column bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Recent messages */}
        <section data-testid="dash-communication">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-slate-300 font-semibold text-[13px]">Recent messages</h2>
            <button
              onClick={() => goToSection('tickets')}
              className="text-emerald-400 hover:text-emerald-300 text-[12px] flex items-center gap-1"
              data-testid="dash-comm-open"
            >
              Open Communication <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {messages.length === 0 ? (
            <div className="text-slate-500 text-[13px] p-4 border border-emerald-900/30 rounded-lg bg-[#0d1210]">
              No messages yet.
            </div>
          ) : (
            <div className="divide-y divide-emerald-900/20 border border-emerald-900/30 rounded-lg bg-[#0d1210]">
              {messages.map(m => (
                <Link
                  key={`${m.ticket_id}-${m.timestamp}`}
                  to={m.link}
                  className="flex items-center gap-3 p-3 hover:bg-[#12181a] transition-colors"
                  data-testid={`dash-comm-row-${m.ticket_id}`}
                >
                  <div className="flex-none relative">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-[11px] font-semibold">
                      {(m.sender || 'S').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    {m.unread && <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-500 border border-[#0d1210]" title="Unread" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-white truncate">{m.sender}</span>
                      <span className="text-[11px] text-slate-500 truncate">· {m.product_name}</span>
                    </div>
                    <div className="text-[11.5px] text-slate-400 truncate">{m.preview}</div>
                  </div>
                  <div className="text-[10.5px] text-slate-500 flex-none">{fmt(m.timestamp)}</div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* DocuVault */}
        <section data-testid="dash-docuvault">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-slate-300 font-semibold text-[13px]">DocuVault — recent documents</h2>
            <div className="flex items-center gap-3">
              {documents.length > 1 && (
                <button
                  onClick={() => {
                    documents.forEach((d, idx) => {
                      setTimeout(() => {
                        const a = document.createElement('a');
                        a.href = d.download_link;
                        a.rel = 'noopener noreferrer';
                        a.download = d.label || 'document';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }, idx * 400);
                    });
                  }}
                  className="text-emerald-400 hover:text-emerald-300 text-[12px] flex items-center gap-1"
                  data-testid="dash-docuvault-download-all"
                >
                  Download all <Download className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => goToSection('docvault')}
                className="text-emerald-400 hover:text-emerald-300 text-[12px] flex items-center gap-1"
                data-testid="dash-docuvault-check-all"
              >
                Check all <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          {documents.length === 0 ? (
            <div className="text-slate-500 text-[13px] p-4 border border-emerald-900/30 rounded-lg bg-[#0d1210]">
              No completed documents yet.
            </div>
          ) : (
            <div className="divide-y divide-emerald-900/20 border border-emerald-900/30 rounded-lg bg-[#0d1210]">
              {documents.map(d => (
                <div key={d.id} className="flex items-center gap-3 p-3" data-testid={`dash-doc-row-${d.id}`}>
                  <div className="flex-none w-8 h-8 rounded bg-red-500/20 text-red-300 flex items-center justify-center text-[9px] font-bold">
                    PDF
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-white truncate">{d.label}</div>
                    {d.uploaded_at && <div className="text-[10.5px] text-slate-500">{fmt(d.uploaded_at)}</div>}
                  </div>
                  <a
                    href={d.download_link}
                    target={d.download_link?.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 text-[12px] flex items-center gap-1"
                    data-testid={`dash-doc-download-${d.id}`}
                  >
                    Download <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
